import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { Contact } from "../models/Contact";
import axios from "../utils/axios";

interface ContactState {
  contacts: Contact[];
  globalContacts: Contact[];
  selectedContact: Contact | null;
  error: { message: string; code: string };
  loading: boolean;
  encryptedGroupKeyResult: string;
  searchResults: Contact[]; // Added search results state
}

const initialState: ContactState = {
  contacts: [
    // {
    //   id: "1",
    //   name: "John Doe",
    //   lastMessage: "Hey, how are you?",
    //   timestamp: "10:30 AM",
    //   unread: 2,
    //   status: "online",
    //   avatar: "/path/to/avatar.jpg",
    //   walletAddress: "0x5df1AaD243CCbC9116e04d7ba5F94c1149653166",
    //   groupId: '',
    // },
    // {
    //   id: "2",
    //   name: "Family Group",
    //   lastMessage: "Mom: Dinner at 7pm",
    //   timestamp: "Yesterday",
    //   unread: 5,
    //   isGroup: true,
    //   walletAddress: "0x5df1AaD243CCbC9116e04d7ba5F94c11496531677",
    //   groupId: "217cafee-a47d-40f2-05ad-08dd96e08217",
    // },
    // ... more contacts
  ],
  selectedContact: null,
  error: { message: "", code: "" },
  loading: false,
  encryptedGroupKeyResult: "",
  searchResults: [],
  globalContacts: [],
};

export interface CreateChatGroupPayload {
  name: string;
}

export const createChatGroup = createAsyncThunk(
  "contacts/createChatGroup",
  async (payload: CreateChatGroupPayload, { rejectWithValue }) => {
    try {
      const { name } = payload;
      const response = await axios.post("api/services/app/ChatGroup/Create", {
        name,
      });

      const data: {
        id: string;
        name: string;
        currentKeyVersion: number;
        tenantId: number;
        encryptedGroupKey: string;
      } = response.data.result;
      return data;
    } catch (error: any) {
      return rejectWithValue({
        message: "Failed to create chat group",
        code: "CREATE_CHAT_GROUP_FAILED",
      });
    }
  }
);

export const createContact = createAsyncThunk(
  "contacts/createContact",
  async (
    payload: { name: string; walletAddress: string },
    { rejectWithValue }
  ) => {
    try {
      const { name, walletAddress } = payload;
      const response = await axios.post("api/services/app/Contact/Create", {
        name,
        walletAddress,
      });
      const data = response.data.result;
      return data;
    } catch (error: any) {
      return rejectWithValue({
        message: "Failed to create contact",
        code: "CREATE_CONTACT_FAILED",
      });
    }
  }
);

export const fetchContacts = createAsyncThunk(
  "contacts/fetchContacts",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        "api/services/app/Contact/GetUsersContacts"
      );
      const data = response.data.result;
      return data;
    } catch (error: any) {
      return rejectWithValue({
        message: "Failed to fetch contacts",
        code: "FETCH_CONTACTS_FAILED",
      });
    }
  }
);

export const fetchGlobalContacts = createAsyncThunk(
  "contacts/fetchGlobalContacts",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        "api/services/app/Contact/GetGlobalContacts"
      );
      const data = response.data.result;
      return data;
    } catch (error: any) {
      return rejectWithValue({
        message: "Failed to fetch global contacts",
        code: "FETCH_CONTACTS_FAILED",
      });
    }
  }
);

export const searchContacts = createAsyncThunk(
  "contacts/searchContacts",
  async (
    payload: { keyword: string },
    { rejectWithValue }
  ) => {
    try {
      const { keyword } = payload;
      const response = await axios.post(
        `api/services/app/User/SearchContacts`,{
          skipCount: 0,
          maxResultCount: 100, 
          keyword: keyword,
          isActive: true
        }
      );
      const data = response.data.result.items;
      return data;
    } catch (error: any) {
      return rejectWithValue({
        message: "Failed to search contacts",
        code: "SEARCH_CONTACTS_FAILED",
      });
    }
  });

  

const contactSlice = createSlice({
  name: "contacts",
  initialState,
  reducers: {
    setContacts: (
      state: ContactState,
      action: PayloadAction<{ contacts: Contact[] }>
    ) => {
      state.contacts = action.payload.contacts;
    },
    addContact: (
      state: ContactState,
      action: PayloadAction<{ contact: Contact }>
    ) => {
      state.contacts.push(action.payload.contact);
    },
    removeContact: (
      state: ContactState,
      action: PayloadAction<{ id: string }>
    ) => {
      state.contacts = state.contacts.filter(
        (contact) => contact.id !== action.payload.id
      );
    },
    selectContact: (
      state: ContactState,
      action: PayloadAction<{ contact: Contact }>
    ) => {
      state.selectedContact = action.payload.contact;
    },
    clearSelectedContact: (state: ContactState) => {
      state.selectedContact = null;
    },
    updateContactStatus: (state, action: PayloadAction<{ 
      userId: string; 
      status: "online" | "offline";
      lastSeen?: string 
    }>) => {
      const { userId, status, lastSeen } = action.payload;
      
        const contactIndex = state.contacts.findIndex(c => 
    c.userId.toString() === userId );
  
  if (contactIndex === -1) {
    // console.warn(`Contact with userId ${userId} not found. Available IDs:`, 
      // state.contacts.map(c => ({ id: c.id, userId: c.userId })));
    return state;
  }
  
 
      return {
    ...state,
    contacts: state.contacts.map((contact, index) => 
      index === contactIndex 
        ? { 
            ...contact, 
            status, 
            ...(lastSeen && { lastSeen }) 
          }
        : contact
    )
  };// Check both possible ID fields



    },
    resetContacts: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(createChatGroup.pending, (state) => {
        state.error = { message: "", code: "" };
        state.loading = true;
      })

      .addCase(createChatGroup.fulfilled, (state, action) => {
        state.contacts.push({
          id: action.payload.name,
          name: action.payload.name,
          lastMessage: "",
          timestamp: "",
          unread: 0,
          isGroup: true,
          walletAddress: "",
          groupId: action.payload.id,
          userId: '',
          lastOnlineTime: "",
          lastMessageNonce:"",
          lastMessageText:"",
          emailAddress: "",
        });
        state.encryptedGroupKeyResult = action.payload.encryptedGroupKey;
        state.loading = false;
      })
      .addCase(createChatGroup.rejected, (state, action) => {
        // state.error = action.payload?.message || "Failed to create chat group";
        state.loading = false;
      })
      .addCase(createContact.pending, (state) => {
        state.error = { message: "", code: "" };
        state.loading = true;
      })
      .addCase(createContact.fulfilled, (state, action) => {
        state.contacts.push(action.payload);
        state.loading = false;
      })
      .addCase(createContact.rejected, (state, action) => {
        //state.error = action.payload ;
        state.loading = false;
      })
      .addCase(fetchContacts.pending, (state) => {
        state.error = { message: "", code: "" };
        state.loading = true;
      })
      .addCase(fetchContacts.fulfilled, (state, action) => {
        state.contacts = action.payload;
        state.loading = false;
      })
      .addCase(fetchContacts.rejected, (state, action) => {
        // state.error = action.payload?.message || "Failed to fetch contacts";
        state.loading = false;
      })
      .addCase(searchContacts.pending, (state) => {
        state.error = { message: "", code: "" };
        state.loading = true;
      })
      .addCase(searchContacts.fulfilled, (state, action) => {
        state.searchResults = action.payload;
        state.loading = false;
      })
      .addCase(searchContacts.rejected, (state, action) => {
        // state.error = action.payload?.message || "Failed to search contacts";
        state.loading = false;
      })
      .addCase(fetchGlobalContacts.pending, (state) => {
        state.error = { message: "", code: "" };
        state.loading = true;
      })
      .addCase(fetchGlobalContacts.fulfilled, (state, action) => {
        state.globalContacts = action.payload;
        state.loading = false;
      })
      .addCase(fetchGlobalContacts.rejected, (state, action) => {
        // state.error = action.payload?.message || "Failed to fetch global contacts";
        state.loading = false;
      });
  },
});

export const {
  setContacts,
  addContact,
  removeContact,
  selectContact,
  clearSelectedContact,
  updateContactStatus,
  resetContacts,
} = contactSlice.actions;
export default contactSlice.reducer;
